import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { shiftAPI } from '../services/employee.service';
import { 
    PlusIcon, 
    PencilIcon, 
    TrashIcon, 
    XMarkIcon, 
    ArrowPathIcon,
    MagnifyingGlassIcon,
    UserCircleIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentArrowDownIcon,
    CalendarIcon,
    UsersIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ====================================================================
// 1. Mock Data Store & Timezone Configuration
// ====================================================================
const TIMEZONES = {
    APAC: {
        'India': { timezone: 'Asia/Kolkata', offset: '+05:30' },
        'Singapore': { timezone: 'Asia/Singapore', offset: '+08:00' },
        'Japan': { timezone: 'Asia/Tokyo', offset: '+09:00' },
        'Australia': { timezone: 'Australia/Sydney', offset: '+11:00' }
    },
    EMEA: {
        'UK': { timezone: 'Europe/London', offset: '+00:00' },
        'Germany': { timezone: 'Europe/Berlin', offset: '+01:00' },
        'UAE': { timezone: 'Asia/Dubai', offset: '+04:00' },
        'South Africa': { timezone: 'Africa/Johannesburg', offset: '+02:00' }
    },
    'North America': {
        'EST': { timezone: 'America/New_York', offset: '-05:00' },
        'CST': { timezone: 'America/Chicago', offset: '-06:00' },
        'PST': { timezone: 'America/Los_Angeles', offset: '-08:00' },
        'Canada': { timezone: 'America/Toronto', offset: '-05:00' }
    }
};

const initialShifts = [
    { id: 'S001', name: 'Morning Fixed', startTime: '09:00', endTime: '18:00', region: 'APAC', country: 'India' },
    { id: 'S002', name: 'Night Fixed', startTime: '22:00', endTime: '07:00', region: 'North America', country: 'EST' },
];

const initialEmployees = [
    { empCode: 'GTPL-101', name: 'Rahul Sharma', assignedShift: 'Morning Fixed', department: 'IT', region: 'APAC', country: 'India' },
    { empCode: 'GTPL-105', name: 'Suresh Kumar', assignedShift: 'Not Assigned', department: 'Admin', region: 'EMEA', country: 'UK' },
    { empCode: 'GTPL-202', name: 'Priya Verma', assignedShift: 'Night Fixed', department: 'Operations', region: 'North America', country: 'EST' },
    { empCode: 'GTPL-301', name: 'Anil Reddy', assignedShift: 'Morning Fixed', department: 'HR', region: 'APAC', country: 'India' },
];

// Report generation utilities
const generateTimeSheetData = (employees, dateRange) => {
    const data = [];
    employees.forEach(emp => {
        dateRange.forEach(date => {
            data.push({
                Date: date,
                EmployeeID: emp.empCode,
                Name: emp.name,
                Department: emp.department,
                Shift: emp.assignedShift,
                Region: emp.region,
                Country: emp.country,
                CheckIn: emp.assignedShift !== 'Not Assigned' ? '09:00' : '-',
                CheckOut: emp.assignedShift !== 'Not Assigned' ? '18:00' : '-',
                WorkingHours: emp.assignedShift !== 'Not Assigned' ? '9.0' : '0.0'
            });
        });
    });
    return data;
};

const getDateRange = (type, customStart, customEnd) => {
    const today = new Date();
    const dates = [];
    let start, end;
    
    switch(type) {
        case 'daily':
            return [today.toISOString().split('T')[0]];
        case 'weekly':
            start = new Date(today.setDate(today.getDate() - today.getDay()));
            end = new Date(today.setDate(today.getDate() - today.getDay() + 6));
            break;
        case 'monthly':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            break;
        case 'quarterly':
            const quarter = Math.floor(today.getMonth() / 3);
            start = new Date(today.getFullYear(), quarter * 3, 1);
            end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
            break;
        case 'half-yearly':
            const half = today.getMonth() < 6 ? 0 : 6;
            start = new Date(today.getFullYear(), half, 1);
            end = new Date(today.getFullYear(), half + 6, 0);
            break;
        case 'yearly':
            start = new Date(today.getFullYear(), 0, 1);
            end = new Date(today.getFullYear(), 11, 31);
            break;
        case 'custom':
            start = new Date(customStart);
            end = new Date(customEnd);
            break;
        default:
            return [today.toISOString().split('T')[0]];
    }
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toISOString().split('T')[0]);
    }
    return dates;
};

// ====================================================================
// 2. Sub-Components (Forms)
// ====================================================================

// --- Enhanced Shift Form with Timezone Support ---
const ShiftFormModal = ({ isOpen, onClose, onSave }) => {
    const [shiftData, setShiftData] = useState({ 
        name: '', 
        startTime: '09:00', 
        endTime: '18:00',
        region: 'APAC',
        country: 'India'
    });
    
    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl">
                        <Dialog.Title className="text-xl font-bold border-b pb-4 text-gray-800">New Shift Setup</Dialog.Title>
                        <div className="mt-6 space-y-4">
                            <input type="text" placeholder="Shift Name" className="w-full border-b-2 border-gray-100 p-2 outline-none focus:border-orange-500" 
                                onChange={(e) => setShiftData({...shiftData, name: e.target.value})} />
                            <div className="grid grid-cols-2 gap-4">
                                <select className="border p-2 rounded" value={shiftData.region} onChange={(e) => setShiftData({...shiftData, region: e.target.value, country: Object.keys(TIMEZONES[e.target.value])[0]})}>
                                    {Object.keys(TIMEZONES).map(region => <option key={region} value={region}>{region}</option>)}
                                </select>
                                <select className="border p-2 rounded" value={shiftData.country} onChange={(e) => setShiftData({...shiftData, country: e.target.value})}>
                                    {Object.keys(TIMEZONES[shiftData.region] || {}).map(country => <option key={country} value={country}>{country}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <input type="time" className="border p-2 rounded" value={shiftData.startTime} onChange={(e) => setShiftData({...shiftData, startTime: e.target.value})} />
                                <input type="time" className="border p-2 rounded" value={shiftData.endTime} onChange={(e) => setShiftData({...shiftData, endTime: e.target.value})} />
                            </div>
                            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                                Timezone: {TIMEZONES[shiftData.region]?.[shiftData.country]?.timezone} ({TIMEZONES[shiftData.region]?.[shiftData.country]?.offset})
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={onClose} className="text-xs font-black text-gray-400 px-4 uppercase">Close</button>
                            <button onClick={() => onSave(shiftData)} className="bg-orange-500 text-white px-6 py-2 rounded-lg text-xs font-black uppercase">Save Shift</button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
};

// --- Enhanced Rotation Setup Form ---
const RotationFormModal = ({ isOpen, onClose, onSave }) => {
    const [rotationData, setRotationData] = useState({
        name: '',
        pattern: 'Weekly',
        department: 'All Departments',
        frequency: '7 Days',
        region: 'APAC',
        country: 'India'
    });

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose} style={{ fontFamily: 'Roboto, sans-serif' }}>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl">
                        <Dialog.Title className="text-xl font-bold border-b pb-4 text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            Configure New Rotation
                        </Dialog.Title>
                        <div className="mt-6 space-y-5">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Rotation Name</label>
                                <input type="text" placeholder="e.g. Monthly Flip" className="w-full border-b-2 border-gray-100 p-2 outline-none focus:border-green-600 text-sm" 
                                    value={rotationData.name} onChange={(e) => setRotationData({...rotationData, name: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Region</label>
                                    <select className="w-full border-b-2 border-gray-100 p-2 text-sm outline-none" 
                                        value={rotationData.region} onChange={(e) => setRotationData({...rotationData, region: e.target.value, country: Object.keys(TIMEZONES[e.target.value])[0]})}>
                                        {Object.keys(TIMEZONES).map(region => <option key={region} value={region}>{region}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Country</label>
                                    <select className="w-full border-b-2 border-gray-100 p-2 text-sm outline-none" 
                                        value={rotationData.country} onChange={(e) => setRotationData({...rotationData, country: e.target.value})}>
                                        {Object.keys(TIMEZONES[rotationData.region] || {}).map(country => <option key={country} value={country}>{country}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Frequency</label>
                                    <select className="w-full border-b-2 border-gray-100 p-2 text-sm outline-none" 
                                        value={rotationData.frequency} onChange={(e) => setRotationData({...rotationData, frequency: e.target.value})}>
                                        <option>7 Days</option>
                                        <option>15 Days</option>
                                        <option>30 Days</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Dept.</label>
                                    <input type="text" placeholder="IT, HR..." className="w-full border-b-2 border-gray-100 p-2 outline-none text-sm" 
                                        value={rotationData.department} onChange={(e) => setRotationData({...rotationData, department: e.target.value})} />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-10">
                            <button onClick={onClose} className="px-5 py-2 text-xs font-black uppercase text-gray-400">Cancel</button>
                            <button onClick={() => onSave(rotationData)} className="px-6 py-2 bg-green-600 text-white rounded-lg text-xs font-black uppercase shadow-lg shadow-green-100">Create Rotation</button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
};

// --- Multi-Employee Assignment Modal ---
const MultiAssignModal = ({ isOpen, onClose, employees, shifts, onSave }) => {
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedShift, setSelectedShift] = useState('');
    
    const toggleEmployee = (empCode) => {
        setSelectedEmployees(prev => 
            prev.includes(empCode) 
                ? prev.filter(id => id !== empCode)
                : [...prev, empCode]
        );
    };
    
    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto">
                        <Dialog.Title className="text-xl font-bold border-b pb-4 text-gray-800">Multi-Employee Assignment</Dialog.Title>
                        
                        <div className="mt-6 space-y-6">
                            <div>
                                <label className="text-sm font-bold text-gray-700 mb-2 block">Select Shift</label>
                                <select className="w-full border p-3 rounded-lg" value={selectedShift} onChange={(e) => setSelectedShift(e.target.value)}>
                                    <option value="">Choose a shift...</option>
                                    {shifts.map(shift => (
                                        <option key={shift.id} value={shift.name}>{shift.name} ({shift.startTime} - {shift.endTime})</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="text-sm font-bold text-gray-700 mb-2 block">Select Employees ({selectedEmployees.length} selected)</label>
                                <div className="max-h-60 overflow-y-auto border rounded-lg">
                                    {employees.map(emp => (
                                        <div key={emp.empCode} className="flex items-center p-3 border-b hover:bg-gray-50">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedEmployees.includes(emp.empCode)}
                                                onChange={() => toggleEmployee(emp.empCode)}
                                                className="mr-3"
                                            />
                                            <div className="flex-1">
                                                <div className="font-bold">{emp.name}</div>
                                                <div className="text-xs text-gray-500">{emp.empCode} - {emp.department}</div>
                                            </div>
                                            <div className="text-xs text-gray-400">{emp.region}/{emp.country}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={onClose} className="text-xs font-black text-gray-400 px-4 uppercase">Cancel</button>
                            <button 
                                onClick={() => onSave(selectedEmployees, selectedShift)} 
                                disabled={!selectedShift || selectedEmployees.length === 0}
                                className="bg-indigo-600 text-white px-6 py-2 rounded-lg text-xs font-black uppercase disabled:bg-gray-300"
                            >
                                Assign to {selectedEmployees.length} Employee(s)
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
};

// --- Reports Modal ---
const ReportsModal = ({ isOpen, onClose, employees }) => {
    const [reportType, setReportType] = useState('daily');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    
    const downloadExcel = () => {
        const dateRange = getDateRange(reportType, customStartDate, customEndDate);
        const data = generateTimeSheetData(employees, dateRange);
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'TimeSheet');
        XLSX.writeFile(wb, `TimeSheet_${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };
    
    const downloadPDF = () => {
        const dateRange = getDateRange(reportType, customStartDate, customEndDate);
        const data = generateTimeSheetData(employees, dateRange);
        const doc = new jsPDF();
        doc.text('Employee TimeSheet Report', 20, 20);
        doc.autoTable({
            head: [['Date', 'Employee ID', 'Name', 'Department', 'Shift', 'Region', 'Check In', 'Check Out', 'Hours']],
            body: data.map(row => [row.Date, row.EmployeeID, row.Name, row.Department, row.Shift, row.Region, row.CheckIn, row.CheckOut, row.WorkingHours])
        });
        doc.save(`TimeSheet_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`);
    };
    
    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl">
                        <Dialog.Title className="text-xl font-bold border-b pb-4 text-gray-800">Generate Reports</Dialog.Title>
                        
                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="text-sm font-bold text-gray-700 mb-2 block">Report Period</label>
                                <select className="w-full border p-3 rounded-lg" value={reportType} onChange={(e) => setReportType(e.target.value)}>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="half-yearly">Half Yearly</option>
                                    <option value="yearly">Yearly</option>
                                    <option value="custom">Custom Date Range</option>
                                </select>
                            </div>
                            
                            {reportType === 'custom' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500">Start Date</label>
                                        <input type="date" className="w-full border p-2 rounded" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500">End Date</label>
                                        <input type="date" className="w-full border p-2 rounded" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-between mt-8">
                            <button onClick={onClose} className="text-xs font-black text-gray-400 px-4 uppercase">Cancel</button>
                            <div className="flex gap-2">
                                <button onClick={downloadExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-black uppercase flex items-center gap-1">
                                    <DocumentArrowDownIcon className="h-4 w-4" /> Excel
                                </button>
                                <button onClick={downloadPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-black uppercase flex items-center gap-1">
                                    <DocumentArrowDownIcon className="h-4 w-4" /> PDF
                                </button>
                            </div>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// 3. Main Component
// ====================================================================
const ShiftRotationSetup = () => {
    const [shifts, setShifts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [rotations, setRotations] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadShifts();
        loadRotations();
        loadEmployees();
    }, []);

    const loadShifts = async () => {
        try {
            setLoading(true);
            const data = await shiftAPI.listShifts();
            const formattedShifts = data.map(s => ({
                id: s[0],
                name: s[1],
                startTime: s[2],
                endTime: s[3],
                region: s[4],
                country: s[5]
            }));
            setShifts(formattedShifts);
        } catch (error) {
            console.error('Failed to load shifts:', error);
            alert('Failed to load shifts');
        } finally {
            setLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const data = await shiftAPI.listEmployees();
            const formattedEmployees = data.map(e => ({
                empCode: e[0],
                name: e[1],
                department: e[2],
                assignedShift: e[3],
                region: e[4],
                country: e[5]
            }));
            setEmployees(formattedEmployees);
        } catch (error) {
            console.error('Failed to load employees:', error);
            alert('Failed to load employees');
        }
    };

    const loadRotations = async () => {
        try {
            const data = await shiftAPI.listRotations();
            setRotations(data);
        } catch (error) {
            console.error('Failed to load rotations:', error);
        }
    };
    
    const [lookupId, setLookupId] = useState("");
    const [autoFoundName, setAutoFoundName] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [isRotationModalOpen, setIsRotationModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isMultiAssignModalOpen, setIsMultiAssignModalOpen] = useState(false);
    const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);

    useEffect(() => {
        const found = employees.find(emp => emp.empCode.toUpperCase() === lookupId.toUpperCase());
        if (found) setAutoFoundName(found.name);
        else setAutoFoundName("");
    }, [lookupId, employees]);

    const filteredEmployees = useMemo(() => {
        return employees.filter(emp => 
            emp.empCode.toLowerCase().includes(searchTerm.toLowerCase()) || 
            emp.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, employees]);

    const handleRotationSave = async (data) => {
        try {
            await shiftAPI.createRotation({
                rotation_name: data.name,
                region: data.region,
                country: data.country,
                frequency: data.frequency.toLowerCase(),
                department: data.department
            });
            setIsRotationModalOpen(false);
            alert(`Rotation "${data.name}" successfully created!`);
            loadRotations();
        } catch (error) {
            console.error('Failed to create rotation:', error);
            alert('Failed to create rotation');
        }
    };
    
    const handleMultiAssign = (employeeIds, shiftName) => {
        setEmployees(employees.map(emp => 
            employeeIds.includes(emp.empCode) 
                ? { ...emp, assignedShift: shiftName }
                : emp
        ));
        setIsMultiAssignModalOpen(false);
        alert(`Successfully assigned ${employeeIds.length} employees to ${shiftName}`);
    };

    return (
        <div className="min-h-screen p-4 sm:p-8 bg-white" style={{ fontFamily: 'Roboto, sans-serif' }}>
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>Shift Management</h1>
                    <p className="text-gray-400 text-sm mt-1 font-medium tracking-tight">Enterprise Shift & Automatic Rotation Control</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setIsShiftModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-2 transition-all text-xs uppercase">
                        <PlusIcon className="h-4 w-4" /> Add Shift
                    </button>
                    <button onClick={() => setIsRotationModalOpen(true)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-2 transition-all text-xs uppercase">
                        <ArrowPathIcon className="h-4 w-4" /> Rotation Setup
                    </button>
                    <button onClick={() => setIsMultiAssignModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-2 transition-all text-xs uppercase">
                        <UsersIcon className="h-4 w-4" /> Multi Assign
                    </button>
                    <button onClick={() => setIsReportsModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-2 transition-all text-xs uppercase">
                        <DocumentArrowDownIcon className="h-4 w-4" /> Reports
                    </button>
                </div>
            </div>

            {/* --- QUICK ASSIGN BY ID (NO CARD BACKGROUND) --- */}
            <div className="mb-16 grid grid-cols-1 md:grid-cols-3 gap-8 items-end">
                <div className="md:col-span-3 border-b border-gray-100 pb-2 flex justify-between">
                    <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>Quick Assign</h2>
                    {rotations.length > 0 && <span className="text-[10px] font-black text-green-600 uppercase tracking-widest bg-green-50 px-2 py-1 rounded">{rotations.length} Active Rotations</span>}
                </div>
                <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Enter Employee ID</label>
                    <div className="relative mt-2">
                        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Type ID..." className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border-b-2 border-gray-100 focus:border-indigo-500 outline-none text-sm font-bold"
                            value={lookupId} onChange={(e) => setLookupId(e.target.value)} />
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Auto-Filled Name</label>
                    <div className="mt-2 flex items-center h-[46px] px-4 border-b-2 border-gray-50 text-sm font-black text-gray-700">
                        {autoFoundName ? (
                            <span className="flex items-center gap-2 text-green-600 italic"><CheckCircleIcon className="h-4 w-4" /> {autoFoundName}</span>
                        ) : (
                            <span className="text-gray-300 font-normal">Waiting for ID...</span>
                        )}
                    </div>
                </div>
                <div>
                    <button disabled={!autoFoundName} onClick={() => {
                        const emp = employees.find(e => e.empCode.toUpperCase() === lookupId.toUpperCase());
                        setSelectedEmp(emp);
                        setIsAssignModalOpen(true);
                    }} className={`w-full py-3 rounded-lg font-black text-xs uppercase tracking-widest transition-all ${autoFoundName ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}>
                        Confirm Assignment
                    </button>
                </div>
            </div>

            {/* --- DATABASE LIST (NO CARD BACKGROUND) --- */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-end mb-6 gap-4 border-b border-gray-50 pb-4">
                    <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>Employee Records</h2>
                    <div className="flex items-center gap-2 border-b border-gray-200">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                        <input type="text" placeholder="Search records..." className="text-sm py-1 outline-none w-48"
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-gray-900">
                                <th className="py-4 text-[11px] font-black text-gray-900 uppercase tracking-tighter w-1/5">Employee ID</th>
                                <th className="py-4 text-[11px] font-black text-gray-900 uppercase tracking-tighter">Profile Details</th>
                                <th className="py-4 text-[11px] font-black text-gray-900 uppercase tracking-tighter">Schedule</th>
                                <th className="py-4 text-[11px] font-black text-gray-900 uppercase tracking-tighter">Region/Timezone</th>
                                <th className="py-4 text-right text-[11px] font-black text-gray-900 uppercase tracking-tighter">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredEmployees.map(emp => (
                                <tr key={emp.empCode} className="group">
                                    <td className="py-5 text-xl font-black text-gray-900 tracking-tighter uppercase">{emp.empCode}</td>
                                    <td className="py-5">
                                        <div className="font-bold text-gray-800 flex items-center gap-1">
                                            {emp.name} {emp.assignedShift.includes('Night') && <ClockIcon className="h-3 w-3 text-indigo-400"/>}
                                        </div>
                                        <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">{emp.department}</div>
                                    </td>
                                    <td className="py-5">
                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black border uppercase ${
                                            emp.assignedShift === 'Not Assigned' ? 'bg-white text-gray-300 border-gray-100' : 'bg-gray-900 text-white border-gray-900'
                                        }`}>
                                            {emp.assignedShift}
                                        </div>
                                    </td>
                                    <td className="py-5">
                                        <div className="flex items-center gap-1">
                                            <GlobeAltIcon className="h-3 w-3 text-gray-400" />
                                            <span className="text-xs font-bold text-gray-600">{emp.region}</span>
                                        </div>
                                        <div className="text-[10px] text-gray-400">{emp.country} ({TIMEZONES[emp.region]?.[emp.country]?.offset})</div>
                                    </td>
                                    <td className="py-5 text-right">
                                        <button onClick={() => { setSelectedEmp(emp); setIsAssignModalOpen(true); }}
                                            className="bg-white border-2 border-gray-900 text-gray-900 px-3 py-1 rounded font-black text-[10px] uppercase hover:bg-gray-900 hover:text-white transition-all">
                                            Modify
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- Modals Integration --- */}
            <RotationFormModal isOpen={isRotationModalOpen} onClose={() => setIsRotationModalOpen(false)} onSave={handleRotationSave} />
            
            <ShiftFormModal isOpen={isShiftModalOpen} onClose={() => setIsShiftModalOpen(false)} onSave={async (data) => {
                try {
                    await shiftAPI.createShift({
                        shift_name: data.name,
                        start_time: data.startTime,
                        end_time: data.endTime,
                        region: data.region,
                        country: data.country,
                        grace_minutes: 15
                    });
                    setIsShiftModalOpen(false);
                    alert('Shift created successfully!');
                    loadShifts();
                } catch (error) {
                    console.error('Failed to create shift:', error);
                    alert('Failed to create shift');
                }
            }} />
            
            <MultiAssignModal 
                isOpen={isMultiAssignModalOpen} 
                onClose={() => setIsMultiAssignModalOpen(false)} 
                employees={employees}
                shifts={shifts}
                onSave={handleMultiAssign}
            />
            
            <ReportsModal 
                isOpen={isReportsModalOpen} 
                onClose={() => setIsReportsModalOpen(false)} 
                employees={employees}
            />

            {/* Shift Assignment Modal */}
            <Transition appear show={isAssignModalOpen} as={React.Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={() => setIsAssignModalOpen(false)}>
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-sm bg-white p-8 rounded-2xl shadow-2xl">
                            <Dialog.Title className="text-xl font-bold text-center mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>Select Schedule</Dialog.Title>
                            <p className="text-center text-xs font-bold text-gray-400 mb-4 uppercase tracking-widest italic">{selectedEmp?.name} ({selectedEmp?.empCode})</p>
                            
                            <div className="mb-4 space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600">Effective From</label>
                                    <input 
                                        type="date" 
                                        id="effectiveFrom"
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full border p-2 rounded text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600">Effective To</label>
                                    <input 
                                        type="date" 
                                        id="effectiveTo"
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full border p-2 rounded text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                {shifts.map(shift => (
                                    <button key={shift.id} onClick={async () => {
                                        const from = document.getElementById('effectiveFrom').value;
                                        const to = document.getElementById('effectiveTo').value;
                                        
                                        if (!from || !to) {
                                            alert('Please select both dates');
                                            return;
                                        }
                                        
                                        try {
                                            await shiftAPI.assignShift({
                                                emp_code: selectedEmp.empCode,
                                                shift_id: shift.id,
                                                effective_from: from,
                                                effective_to: to
                                            });
                                            
                                            setEmployees(employees.map(a => a.empCode === selectedEmp.empCode ? { ...a, assignedShift: shift.name } : a));
                                            setIsAssignModalOpen(false);
                                            setLookupId("");
                                            alert('Shift assigned successfully!');
                                            loadEmployees();
                                        } catch (error) {
                                            console.error('Failed to assign shift:', error);
                                            alert('Failed to assign shift');
                                        }
                                    }} className="w-full text-left p-4 border-2 border-gray-50 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all flex justify-between items-center group">
                                        <span className="font-black text-gray-700 group-hover:text-indigo-700 text-sm uppercase">{shift.name}</span>
                                        <ArrowPathIcon className="h-4 w-4 text-gray-300 group-hover:text-indigo-500" />
                                    </button>
                                ))}
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default ShiftRotationSetup;